/**
 * <cart-scroll-indicator> — a persistent scroll indicator for the cart drawer's item list.
 *
 * WHY THIS EXISTS
 * iOS Safari does not implement `::-webkit-scrollbar`; its scroll indicators are drawn by the
 * system and are transient by platform design, so the CSS fix in custom.css (which does make the
 * scrollbar permanent in desktop Safari and Chromium) cannot reach it. This draws our own.
 *
 * SCOPE
 * Only runs on touch devices — `(hover: none) and (pointer: coarse)` — which is exactly where the
 * native scrollbar is an overlay that fades. Desktop browsers keep their native scrollbar and never
 * execute past the guard. The matching CSS uses the same media query, so the two cannot disagree.
 *
 * LAYOUT SAFETY
 * The element is absolutely positioned and `pointer-events: none`, so it is out of flow and cannot
 * intercept touches. It sits *outside* the scroll container on purpose: `<scroll-hint>` sets a
 * `mask-image` on itself, which would fade our thumb along with the content.
 *
 * MORPH SAFETY
 * Horizon re-renders the drawer through `morphSection` on every cart mutation, which can swap the
 * scroll container underneath us. So the target is resolved lazily on each update rather than
 * cached, the scroll listener is attached at the document in the capture phase (scroll events do
 * not bubble, but they do capture), and `cart:update` triggers a re-measure because a morph changes
 * the content height without resizing the container.
 */

const TOUCH_QUERY = '(hover: none) and (pointer: coarse)';
const MIN_THUMB_HEIGHT = 28;

class CartScrollIndicator extends HTMLElement {
  /** @type {number | null} */
  #rafId = null;

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  #active = false;

  connectedCallback() {
    // Desktop keeps its native scrollbar: bail before touching anything.
    if (!window.matchMedia?.(TOUCH_QUERY).matches) return;
    if (this.#active) return;
    this.#active = true;

    document.addEventListener('scroll', this.#handleScroll, { capture: true, passive: true });
    document.addEventListener('cart:update', this.#schedule);

    if ('ResizeObserver' in window) {
      this.#resizeObserver = new ResizeObserver(this.#schedule);
      const target = this.#target;
      if (target) this.#resizeObserver.observe(target);
    }

    this.#schedule();
  }

  disconnectedCallback() {
    if (!this.#active) return;
    this.#active = false;

    document.removeEventListener('scroll', this.#handleScroll, { capture: true });
    document.removeEventListener('cart:update', this.#schedule);

    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;

    if (this.#rafId !== null) {
      cancelAnimationFrame(this.#rafId);
      this.#rafId = null;
    }
  }

  /**
   * The scroll container this indicator represents. Resolved on every read so a morph that
   * replaces the container does not leave us pointing at a detached node.
   * @returns {HTMLElement | null}
   */
  get #target() {
    return this.parentElement?.querySelector('.cart-drawer__items') ?? null;
  }

  /** @returns {HTMLElement | null} */
  get #thumb() {
    return this.querySelector('.cart-scroll-indicator__thumb');
  }

  /** @param {Event} event */
  #handleScroll = (event) => {
    if (event.target !== this.#target) return;
    this.#schedule();
  };

  #schedule = () => {
    if (this.#rafId !== null) return;

    this.#rafId = requestAnimationFrame(() => {
      this.#rafId = null;
      this.#update();
    });
  };

  #update = () => {
    const target = this.#target;
    const thumb = this.#thumb;
    if (!target || !thumb) return;

    const { scrollTop, scrollHeight, clientHeight, offsetTop } = target;
    const overflow = scrollHeight - clientHeight;

    // Nothing to scroll (or the drawer is closed, so clientHeight is 0) — show nothing.
    if (overflow <= 1) {
      this.removeAttribute('data-visible');
      return;
    }

    const thumbHeight = Math.max(MIN_THUMB_HEIGHT, (clientHeight / scrollHeight) * clientHeight);
    const travel = Math.max(0, clientHeight - thumbHeight);
    const progress = Math.min(1, Math.max(0, scrollTop / overflow));

    // Track is aligned to the scroll container, which is a sibling in the same positioned parent.
    this.style.top = `${offsetTop}px`;
    this.style.height = `${clientHeight}px`;
    thumb.style.height = `${thumbHeight}px`;
    thumb.style.transform = `translateY(${progress * travel}px)`;

    this.setAttribute('data-visible', '');
  };
}

if (!customElements.get('cart-scroll-indicator')) {
  customElements.define('cart-scroll-indicator', CartScrollIndicator);
}
